import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useScrollReveal from '@/hooks/useScrollReveal';
import { products130g, products440g, products950g, type Product } from '@/mocks/products';

const tabs = [
  { key: 'catalog.tab_130', products: products130g },
  { key: 'catalog.tab_440', products: products440g },
  { key: 'catalog.tab_950', products: products950g },
];

function ProductModal({ product, onClose, t }: { product: Product; onClose: () => void; t: (k: string) => string }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-foreground-900/40 backdrop-blur-sm" />
      <div
        className="relative bg-background-50 rounded-2xl max-w-[640px] w-full max-h-[90vh] overflow-y-auto shadow-[0_8px_40px_rgba(0,0,0,0.12)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors z-10"
        >
          <i className="ri-close-line text-lg text-foreground-600"></i>
        </button>

        <div className="aspect-[4/3] bg-white overflow-hidden rounded-t-2xl">
          <img
            src={product.imageUrl}
            alt={t(product.nameKey)}
            className="w-full h-full object-contain"
          />
        </div>

        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-1">
                {t(product.nameKey)}
              </h3>
              <span className="inline-block text-sm font-medium text-primary-500 bg-primary-100 px-3 py-1 rounded-full">
                {product.size}
              </span>
              {product.isVegan && (
                <span className="inline-block ml-2 text-sm font-medium text-accent-600 bg-accent-100 px-3 py-1 rounded-full">
                  Vegano
                </span>
              )}
            </div>
          </div>

          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
            {t(product.descKey)}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-background-100 rounded-xl p-4">
              <h4 className="font-heading text-sm font-semibold text-foreground-800 mb-2">{t('catalog.ingredients')}</h4>
              <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                {product.ingredients}
              </p>
            </div>
            <div className="bg-background-100 rounded-xl p-4">
              <h4 className="font-heading text-sm font-semibold text-foreground-800 mb-2">{t('catalog.benefits')}</h4>
              <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                Alto en proteína, probióticos naturales, deslactosado{product.id.includes('sandia') ? '.' : ', sin azúcar añadida.'}
              </p>
            </div>
            <div className="bg-background-100 rounded-xl p-4">
              <h4 className="font-heading text-sm font-semibold text-foreground-800 mb-2">{t('catalog.storage')}</h4>
              <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                Mantener refrigerado entre 2°C y 6°C. Consumir antes de la fecha indicada en el empaque.
              </p>
            </div>
            <div className="bg-background-100 rounded-xl p-4">
              <h4 className="font-heading text-sm font-semibold text-foreground-800 mb-2">{t('catalog.consumption')}</h4>
              <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                Disfrutar solo, con frutas frescas, granola, miel o como ingrediente en batidos y recetas.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CatalogSection() {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollReveal(0.05);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  return (
    <section id="catalogo" className="py-20 md:py-28 bg-background-50 relative overflow-hidden">
      {/* Greek decorative */}
      <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
        <div className="absolute top-20 -right-10 opacity-[0.04]">
          <div className="flex flex-col gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="w-16 h-4 border border-primary-400 rounded-full"></div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-10">
        <div ref={ref} className={`scroll-reveal text-center mb-12 ${isVisible ? 'visible' : ''}`}>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-4">
            {t('catalog.title')}
          </h2>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-background-100 rounded-full p-1 gap-1">
            {tabs.map((tab, idx) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(idx)}
                className={`whitespace-nowrap px-6 py-2.5 text-sm font-medium rounded-full transition-all duration-300 ${
                  activeTab === idx
                    ? 'bg-primary-500 text-background-50 shadow-[0_2px_8px_rgba(0,0,0,0.08)]'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {t(tab.key)}
              </button>
            ))}
          </div>
        </div>

        {/* Product grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
          {tabs[activeTab].products.map((product, idx) => (
            <div
              key={product.id}
              className="scroll-reveal group bg-background-50 rounded-xl border border-background-200 overflow-hidden cursor-pointer transition-all duration-500 hover:-translate-y-1.5 hover:border-primary-200"
              style={{ transitionDelay: `${idx * 60}ms` }}
              onClick={() => setSelectedProduct(product)}
            >
              <div className="aspect-square bg-white overflow-hidden">
                <img
                  src={product.imageUrl}
                  alt={t(product.nameKey)}
                  className="w-full h-full object-contain p-3 transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-4 text-center">
                <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-800 mb-1 group-hover:text-primary-500 transition-colors">
                  {t(product.nameKey)}
                </h3>
                <span className="text-xs text-foreground-400">{product.size}</span>
                {product.isVegan && (
                  <span className="inline-block ml-1 text-xs px-1.5 py-0.5 bg-accent-100 text-accent-600 rounded-full">Vegano</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Product modal */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          t={t}
        />
      )}
    </section>
  );
}